import { useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from "react";

// Chrome Dino-style endless runner using Canvas
// Logical resolution: 900x500 (1.8:1) — taller than classic dino so mobile screens
// get a comfortable playable area while still keeping the "runner" feel.
// Handles: keyboard (space/up/down), touch swipes
// Exposes imperative API: start(), resume(), retry(), currentScore(), isRunning(), stop()

const LOGICAL_W = 900;
const LOGICAL_H = 500;

const GRAVITY = 0.95;
const JUMP_VELOCITY = -17;
const GROUND_Y = LOGICAL_H - 50;
const PLAYER_X = 80;
const PLAYER_W = 44;
const PLAYER_H = 56;
const INITIAL_SPEED = 7;
const MAX_SPEED = 28;
// Tiered difficulty curve — piecewise linear speed interpolation between
// waypoints, then STEPPY tier callouts on boundary crossings so the player
// FEELS the escalation instead of a flat plateau. Each tier tightens the
// jump-timing window without touching the physics-safe gap formula below.
//   tier 1  (0-1000)     : warm-up, minimal ramp
//   tier 2  (1000-2500)  : medium — noticeable pickup
//   tier 3  (2500-4000)  : fast
//   tier 4  (4000-6000)  : faster
//   tier 5  (6000-8500)  : intense
//   tier 6  (8500-12000) : hyper
//   tier 7  (12000-17000): insane
//   tier 8  (17000-25000): endless — small residual ramp so 20k+ runs still
//                          feel different from 10k runs (no dead plateau)
//   tier 9  (25000+)     : cap at MAX_SPEED (still no infinite plateau since
//                          scoring rate itself scales with speed)
const SPEED_TIERS = [
  { score: 0, speed: 7.0, label: "WARM-UP" },
  { score: 1000, speed: 8.6, label: "MEDIUM" },
  { score: 2500, speed: 11.0, label: "FAST" },
  { score: 4000, speed: 13.5, label: "FASTER" },
  { score: 6000, speed: 16.0, label: "INTENSE" },
  { score: 8500, speed: 19.0, label: "HYPER" },
  { score: 12000, speed: 22.0, label: "INSANE" },
  { score: 17000, speed: 25.0, label: "ENDLESS" },
  { score: 25000, speed: MAX_SPEED, label: "GODMODE" },
];

/**
 * Piecewise-linear speed from the SPEED_TIERS waypoints. Returns both the
 * interpolated speed AND the tier index the score currently sits in — the
 * caller uses the tier index to fire a milestone flash on boundary crossings.
 */
function _speedForScore(score) {
  let tierIdx = 0;
  for (let i = SPEED_TIERS.length - 1; i >= 0; i--) {
    if (score >= SPEED_TIERS[i].score) {
      tierIdx = i;
      break;
    }
  }
  if (tierIdx === SPEED_TIERS.length - 1) {
    return { speed: SPEED_TIERS[tierIdx].speed, tierIdx };
  }
  const cur = SPEED_TIERS[tierIdx];
  const next = SPEED_TIERS[tierIdx + 1];
  const t = (score - cur.score) / (next.score - cur.score);
  return { speed: cur.speed + (next.speed - cur.speed) * t, tierIdx };
}
const INVINCIBILITY_MS = 2000; // grace period after Resume-with-Ad
const MAX_SCORE_CAP = 99999; // game auto-ends here — real players can't legitimately hit this
const MAX_EVENTS = 1500; // safety cap on recorded input timeline

// Rain event tunables (user spec: 7s duration, covers full sky down to
// the ground track). Trigger step widens as the score climbs so late-game
// rain doesn't feel spammy:
//   score <  2500  →  every 500
//   score <  7000  →  every 1000
//   score >= 7000  →  every 1500
const RAIN_DURATION_MS = 7000;
const RAIN_BASE_Y = GROUND_Y; // = 450, i.e. rain covers dragons + full sky
function _rainStepForScore(score) {
  if (score < 2500) return 500;
  if (score < 7000) return 1000;
  return 1500;
}
// Milestone SFX cadence — same widening pattern so the arpeggio doesn't
// hammer the player at high score:
//   score <  2500  →  every 100
//   score <  5000  →  every 500
//   score >= 5000  →  every 1000
function _milestoneStepForScore(score) {
  if (score < 2500) return 100;
  if (score < 5000) return 500;
  return 1000;
}

const GameCanvas = forwardRef(function GameCanvas({ onGameOver, onScoreMilestone }, ref) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);

  const initState = useCallback(() => {
    // Pre-seed a few clouds at random x positions across the sky so the
    // scene is populated the instant the run begins (no empty start).
    const seededClouds = [];
    for (let i = 0; i < 6; i++) {
      seededClouds.push({
        x: Math.random() * LOGICAL_W,
        y: 30 + Math.random() * 80,
        w: 40 + Math.random() * 30,
        speed: 1.3 + Math.random() * 0.9,
      });
    }
    stateRef.current = {
      running: false,
      paused: false,
      score: 0,
      speed: INITIAL_SPEED,
      player: {
        y: GROUND_Y - PLAYER_H,
        vy: 0,
        jumping: false,
        h: PLAYER_H,
      },
      obstacles: [],
      clouds: seededClouds,
      particles: [],
      distanceToNext: 300,
      rainUntil: 0,
      shakeUntil: 0,
      flashUntil: 0,
      invincibleUntil: 0,
      nextRainAt: 500,
      nextMilestoneAt: 100,
      lastTierIdx: 0,
      lastTime: 0,
      rafId: null,
      // Anti-cheat: input event timeline. Each entry = {t, s, type}
      // t = ms since game start, s = score at that moment, type = "jump"
      events: [],
      startPerf: 0,
      // Anti-cheat: count of obstacles the player has SUCCESSFULLY cleared
      // (obstacle scrolled off-screen past the dino without a collision).
      // Server cross-verifies this vs. the final score.
      obstaclesPassed: 0,
      _clearedObstacleIds: 0,
    };
  }, []);

  const _record = (type) => {
    const s = stateRef.current;
    if (!s || !s.running || s.events.length >= MAX_EVENTS) return;
    s.events.push({
      t: Math.round(performance.now() - s.startPerf),
      s: Math.floor(s.score),
      type,
    });
  };

  const jump = useCallback(() => {
    const s = stateRef.current;
    if (!s || !s.running || s.paused) return;
    if (!s.player.jumping) {
      s.player.vy = JUMP_VELOCITY;
      s.player.jumping = true;
      _record("jump");
    }
  }, []);

  const spawnObstacle = useCallback(() => {
    const s = stateRef.current;
    // 65% cactus, 35% dragon (flying) once score > 200
    const canFly = s.score > 200;
    const isDragon = canFly && Math.random() < 0.35;
    if (isDragon) {
      const y = GROUND_Y - 75 - Math.floor(Math.random() * 2) * 20;
      s.obstacles.push({
        type: "dragon",
        x: LOGICAL_W + 10,
        y,
        w: 56,
        h: 32,
        flap: 0,
      });
    } else {
      const variant = Math.floor(Math.random() * 3);
      const w = variant === 2 ? 52 : 30;
      const h = variant === 0 ? 46 : variant === 1 ? 58 : 46;
      s.obstacles.push({
        type: "cactus",
        x: LOGICAL_W + 10,
        y: GROUND_Y - h,
        w,
        h,
        variant,
      });
    }
  }, []);

  const spawnCloud = useCallback(() => {
    const s = stateRef.current;
    s.clouds.push({
      x: LOGICAL_W + 10,
      y: 30 + Math.random() * 80,
      w: 40 + Math.random() * 30,
      speed: 1.3 + Math.random() * 0.9, // faster drift — no more stalled clouds
    });
  }, []);

  // Ground-level dust particles — small 2x2 dots that live in the empty
  // strip BELOW the ground line (between y=GROUND_Y+3 and y=LOGICAL_H-3).
  // They drift left at ~game speed so the run feels grounded and alive.
  const spawnParticle = useCallback(() => {
    const s = stateRef.current;
    const stripTop = GROUND_Y + 4;
    const stripBottom = LOGICAL_H - 4;
    s.particles.push({
      x: LOGICAL_W + 4,
      y: stripTop + Math.random() * (stripBottom - stripTop),
      size: Math.random() < 0.7 ? 2 : 3,
      // Each dot has its own speed multiplier so the strip has parallax feel
      speedMul: 0.7 + Math.random() * 0.6,
    });
  }, []);

  const draw = useCallback((ctx) => {
    const s = stateRef.current;
    // clear
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H);

    const now = performance.now();

    // Background lines removed — only clouds, obstacles, and player remain
    // inside the canvas box (single static ground line below).

    // clouds — soft, static-ish drift (very low luminance)
    ctx.fillStyle = "rgba(0, 240, 255, 0.18)";
    s.clouds.forEach((c) => {
      ctx.fillRect(c.x, c.y, c.w, 8);
      ctx.fillRect(c.x + 6, c.y - 4, c.w - 12, 4);
    });

    // ground — single static line, softened for reduced eye strain
    ctx.strokeStyle = "#5A7A88";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, GROUND_Y);
    ctx.lineTo(LOGICAL_W, GROUND_Y);
    ctx.stroke();

    // ground dust particles — small dots in the strip below the track (softened)
    ctx.fillStyle = "rgba(90, 122, 136, 0.55)";
    s.particles.forEach((pt) => {
      ctx.fillRect(pt.x, pt.y, pt.size, pt.size);
    });

    // obstacles — matte slate palette (soft on the eyes, no harsh neon glow)
    // Any residual canvas shadow is explicitly cleared so obstacles NEVER
    // render with a glow — focal depth stays on the player only.
    ctx.shadowBlur = 0;
    ctx.shadowColor = "transparent";
    s.obstacles.forEach((o) => {
      if (o.type === "cactus") {
        // Soft slate — matte, low-luminance, no eye strain
        ctx.fillStyle = "#8A9BA8";
        ctx.fillRect(o.x, o.y, o.w, o.h);
        // Subtle inner shading for depth (kept dark & flat, no glow)
        ctx.fillStyle = "rgba(20, 26, 34, 0.35)";
        ctx.fillRect(o.x + 3, o.y + 4, o.w - 6, 3);
        ctx.fillRect(o.x + 3, o.y + 14, o.w - 6, 3);
      } else {
        // dragon — bolder teal (slightly more saturated for visibility)
        o.flap = (o.flap || 0) + 0.2;
        const wingUp = Math.sin(o.flap) > 0;
        ctx.fillStyle = "#4FA8BC";
        ctx.fillRect(o.x, o.y + 6, o.w, o.h - 12);
        // head
        ctx.fillRect(o.x + o.w - 6, o.y + 4, 8, 10);
        // wings — accent uses a coherent bolder rose that reads clearly
        ctx.fillStyle = wingUp ? "#4FA8BC" : "#D9647A";
        ctx.fillRect(o.x + 8, o.y - (wingUp ? 6 : 0), 20, wingUp ? 6 : 4);
        ctx.fillRect(o.x + 8, o.y + o.h - (wingUp ? 0 : 6), 20, wingUp ? 4 : 6);
        // eye
        ctx.fillStyle = "#050505";
        ctx.fillRect(o.x + o.w - 2, o.y + 6, 3, 3);
      }
    });

    // player (with invincibility blink) — subtle glow ONLY on the player
    // for focal depth. All other draws must reset shadowBlur to 0 before
    // rendering.
    const p = s.player;
    const invincible = performance.now() < s.invincibleUntil;
    // Blink: hide every ~120ms during grace period
    const blinkVisible = !invincible || Math.floor(performance.now() / 120) % 2 === 0;
    if (blinkVisible) {
      // Subtle character glow — bolder rose for better visibility
      ctx.shadowColor = invincible ? "rgba(79, 168, 188, 0.5)" : "rgba(217, 100, 122, 0.4)";
      ctx.shadowBlur = 10;
      ctx.fillStyle = invincible ? "#FFFFFF" : "#D9647A";
      ctx.fillRect(PLAYER_X, p.y, PLAYER_W, p.h);
      // Reset glow before drawing crisp facial highlights
      ctx.shadowBlur = 0;
      ctx.shadowColor = "transparent";
      // player highlight
      ctx.fillStyle = invincible ? "#4FA8BC" : "#FFFFFF";
      ctx.fillRect(PLAYER_X + 4, p.y + 4, 4, 4); // eye
      ctx.fillStyle = "#EAEB18";
      ctx.fillRect(PLAYER_X + PLAYER_W - 6, p.y + p.h - 6, 4, 4);
    }
    // Shield ring effect while invincible
    if (invincible) {
      ctx.strokeStyle = "rgba(79, 168, 188, 0.7)";
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(PLAYER_X - 4, p.y - 4, PLAYER_W + 8, p.h + 8);
      ctx.setLineDash([]);
    }

    // rain event — 7s window triggered every 500 pts. Streaks fill the
    // full sky from y=0 down to y=RAIN_BASE_Y (= GROUND_Y = 450), so
    // rain passes through the dragon flight lane naturally. The clip
    // region strictly stops AT the ground line — no bleed into the
    // bottom padding strip.
    const rainActive = now < s.rainUntil;
    if (rainActive) {
      // Very subtle atmospheric tint on the sky region only
      ctx.fillStyle = "rgba(0, 240, 255, 0.04)";
      ctx.fillRect(0, 0, LOGICAL_W, RAIN_BASE_Y);
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, LOGICAL_W, RAIN_BASE_Y);
      ctx.clip();
      ctx.strokeStyle = "rgba(0, 240, 255, 0.28)";
      ctx.lineWidth = 1;
      const rainOffset = (now / 30) % 20;
      for (let i = 0; i < 60; i++) {
        const x = (i * 17 + rainOffset * 3) % LOGICAL_W;
        // Confine spawn Y strictly to the sky region above dragons.
        const y = (i * 23 + rainOffset * 8) % RAIN_BASE_Y;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - 3, y + 10);
        ctx.stroke();
      }
      ctx.restore();
      // banner
      ctx.fillStyle = "#00F0FF";
      ctx.font = "bold 16px 'IBM Plex Mono', monospace";
      ctx.fillText("⚡ RAIN EVENT ⚡", LOGICAL_W / 2 - 80, 30);
    }

    // score (in-canvas — also mirrored above canvas in React UI)
    ctx.fillStyle = now < s.flashUntil ? "#EAEB18" : "#E2E8F0";
    ctx.font = "bold 32px 'IBM Plex Mono', monospace";
    const scoreText = String(Math.floor(s.score)).padStart(5, "0");
    ctx.fillText(scoreText, LOGICAL_W - 140, 44);

    // hint
    ctx.fillStyle = "rgba(148, 163, 184, 0.6)";
    ctx.font = "14px 'IBM Plex Mono', monospace";
    ctx.fillText("SPACE / ↑ / TAP  ·  JUMP", 16, 28);
  }, []);

  const gameLoop = useCallback(
    (time) => {
      const s = stateRef.current;
      if (!s || !s.running) return;
      if (s.paused) {
        s.rafId = requestAnimationFrame(gameLoop);
        return;
      }
      const dt = s.lastTime ? Math.min(time - s.lastTime, 40) : 16;
      s.lastTime = time;
      const factor = dt / 16;

      // Tiered speed curve — see SPEED_TIERS above. Piecewise linear
      // interpolation gives smooth-inside-tier progression, and the
      // boundary crossings emit a tier-up milestone so the player FEELS
      // the difficulty change (screen flash + tier callout) instead of
      // an invisible ramp. Score above 25000 tops out at MAX_SPEED so the
      // physics-safe gap formula stays honorable; scoring itself continues
      // to accelerate because s.score gains speed-scaled points below.
      const { speed: nextSpeed, tierIdx } = _speedForScore(s.score);
      s.speed = Math.min(MAX_SPEED, nextSpeed);
      if (tierIdx > s.lastTierIdx) {
        s.lastTierIdx = tierIdx;
        s.flashUntil = performance.now() + 600;
        s.shakeUntil = performance.now() + 220;
        onScoreMilestone &&
          onScoreMilestone("tier", tierIdx, SPEED_TIERS[tierIdx].label);
      }

      // score
      s.score += 0.5 * factor * (s.speed / INITIAL_SPEED);

      // MAX SCORE CAP — auto-end run at 99999 (real players can't hit this;
      // a client-side score-inflation hack still gets capped here).
      if (s.score >= MAX_SCORE_CAP) {
        s.score = MAX_SCORE_CAP;
        s.running = false;
        const canvas = canvasRef.current;
        if (canvas) canvas.classList.remove("shake");
        onGameOver && onGameOver({ score: MAX_SCORE_CAP, capped: true });
        return;
      }

      // milestones — cadence widens as score climbs (every 100 → 500 → 1000)
      while (Math.floor(s.score) >= s.nextMilestoneAt) {
        const hitScore = s.nextMilestoneAt;
        s.flashUntil = performance.now() + 800;
        onScoreMilestone && onScoreMilestone("milestone", hitScore);
        s.nextMilestoneAt = hitScore + _milestoneStepForScore(hitScore);
      }
      // rain events — cadence widens as score climbs (500 → 1000 → 1500),
      // 7s duration, only above dragon base
      while (Math.floor(s.score) >= s.nextRainAt) {
        const hitScore = s.nextRainAt;
        s.rainUntil = performance.now() + RAIN_DURATION_MS;
        s.shakeUntil = performance.now() + 500;
        onScoreMilestone && onScoreMilestone("rain", hitScore);
        s.nextRainAt = hitScore + _rainStepForScore(hitScore);
      }

      // player physics
      const p = s.player;
      p.vy += GRAVITY * factor;
      p.y += p.vy * factor;
      const groundTop = GROUND_Y - p.h;
      if (p.y >= groundTop) {
        p.y = groundTop;
        p.vy = 0;
        p.jumping = false;
      }

      // spawn obstacles
      s.distanceToNext -= s.speed * factor;
      if (s.distanceToNext <= 0) {
        spawnObstacle();
        // Physics-safe minimum gap, tuned tighter than the previous
        // BASE_GAP + K*speed formula so early-game (speed 7) obstacles feel
        // pleasantly close instead of oceans apart, while high-speed runs
        // still leave enough room to complete the jump-and-land arc.
        //
        // Safety derivation:
        //   Full jump flight ≈ 2·|JUMP_VELOCITY|/GRAVITY ≈ 35.8 frames.
        //   During flight the world scrolls left by 35.8·speed pixels.
        //   For the NEXT obstacle to arrive AFTER the player has landed,
        //   we need minGap ≥ 35.8·speed + PLAYER_W (safety margin).
        // At MAX_SPEED=28 this needs minGap ≥ ~1046 → we pick K=38 so
        // 40 + 28·38 = 1104 which comfortably clears the threshold.
        const BASE_GAP = 40;
        const SPEED_GAP_K = 38;
        const minGap = BASE_GAP + s.speed * SPEED_GAP_K;
        // Random jitter shrinks with speed so high-speed rhythm feels
        // consistent while low-speed early game has natural variety.
        const jitter = Math.max(80, 220 - s.speed * 6);
        s.distanceToNext = minGap + Math.random() * jitter;
      }

      // move obstacles & collision
      s.obstacles.forEach((o) => (o.x -= s.speed * factor));
      // Any obstacle that scrolled fully off-screen to the left = successfully
      // cleared by the player. Count it before we drop it from the array.
      const before = s.obstacles.length;
      s.obstacles = s.obstacles.filter((o) => o.x + o.w > -10);
      const cleared = before - s.obstacles.length;
      if (cleared > 0) s.obstaclesPassed += cleared;

      // clouds — spawn more frequently for a fuller sky, still parallax drift
      if (Math.random() < 0.018 * factor) spawnCloud();
      s.clouds.forEach((c) => (c.x -= c.speed * factor));
      s.clouds = s.clouds.filter((c) => c.x + c.w > -10);

      // ground particles — moving dust dots in the strip below the track,
      // spawned frequently so the strip always feels alive
      if (Math.random() < 0.15 * factor) spawnParticle();
      s.particles.forEach((pt) => (pt.x -= s.speed * pt.speedMul * factor));
      s.particles = s.particles.filter((pt) => pt.x + pt.size > -4);

      // collision detection (AABB) — skipped during invincibility grace.
      // Hitbox padding: 1px inner padding added on top of the existing 4/2 px
      // inset so tiny "edge kiss" pixels no longer count as a fatal hit — a
      // player only dies when they meaningfully overlap the obstacle body.
      const isInvincible = performance.now() < s.invincibleUntil;
      if (!isInvincible) {
        for (const o of s.obstacles) {
          const px1 = PLAYER_X + 5;
          const py1 = p.y + 3;
          const px2 = PLAYER_X + PLAYER_W - 5;
          const py2 = p.y + p.h - 3;
          const ox1 = o.x + 3;
          const oy1 = o.y + 3;
          const ox2 = o.x + o.w - 3;
          const oy2 = o.y + o.h - 3;
          if (px1 < ox2 && px2 > ox1 && py1 < oy2 && py2 > oy1) {
            s.running = false;
            const canvas = canvasRef.current;
            if (canvas) canvas.classList.remove("shake");
            onGameOver && onGameOver({ score: Math.floor(s.score) });
            return;
          }
        }
      }

      // shake
      const canvas = canvasRef.current;
      if (canvas) {
        if (performance.now() < s.shakeUntil) canvas.classList.add("shake");
        else canvas.classList.remove("shake");
      }

      // draw
      const ctx = canvas.getContext("2d");
      draw(ctx);

      s.rafId = requestAnimationFrame(gameLoop);
    },
    [draw, onGameOver, onScoreMilestone, spawnCloud, spawnObstacle, spawnParticle]
  );

  const start = useCallback(() => {
    initState();
    const s = stateRef.current;
    s.running = true;
    s.lastTime = 0;
    s.startPerf = performance.now();
    s.rafId = requestAnimationFrame(gameLoop);
  }, [gameLoop, initState]);

  const retry = useCallback(() => {
    if (stateRef.current?.rafId) cancelAnimationFrame(stateRef.current.rafId);
    start();
  }, [start]);

  const resume = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    // Clear nearby obstacles so player doesn't die instantly
    s.obstacles = s.obstacles.filter((o) => o.x > PLAYER_X + PLAYER_W + 120);
    s.distanceToNext = Math.max(s.distanceToNext, 220);
    // 2-second invincibility grace period
    s.invincibleUntil = performance.now() + INVINCIBILITY_MS;
    s.running = true;
    s.lastTime = 0;
    if (s.rafId) cancelAnimationFrame(s.rafId);
    s.rafId = requestAnimationFrame(gameLoop);
  }, [gameLoop]);

  useImperativeHandle(
    ref,
    () => ({
      start,
      retry,
      resume,
      currentScore: () => Math.floor(stateRef.current?.score || 0),
      currentSpeed: () => stateRef.current?.speed || INITIAL_SPEED,
      isRunning: () => !!stateRef.current?.running,
      getEvents: () => (stateRef.current?.events || []).slice(),
      obstaclesPassed: () => stateRef.current?.obstaclesPassed || 0,
      stop: () => {
        const s = stateRef.current;
        if (s?.rafId) cancelAnimationFrame(s.rafId);
        if (s) s.running = false;
      },
    }),
    [start, retry, resume]
  );

  // Setup canvas + inputs
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Set the internal resolution; CSS handles scaling
    canvas.width = LOGICAL_W;
    canvas.height = LOGICAL_H;
    const ctx = canvas.getContext("2d");
    initState();
    // initial screen
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H);
    ctx.fillStyle = "#00F0FF";
    ctx.font = "bold 24px 'IBM Plex Mono', monospace";
    ctx.fillText("PRESS SPACE OR TAP TO START", LOGICAL_W / 2 - 180, LOGICAL_H / 2);

    const onKeyDown = (e) => {
      if (e.code === "Space" || e.code === "ArrowUp") {
        e.preventDefault();
        jump();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    // Touch swipes
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    const onTouchStart = (e) => {
      const t = e.touches[0];
      touchStartX = t.clientX;
      touchStartY = t.clientY;
      touchStartTime = performance.now();
    };
    const onTouchEnd = (e) => {
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStartX;
      const dy = t.clientY - touchStartY;
      const dt = performance.now() - touchStartTime;
      // Any tap OR upward swipe = jump. Downward swipes are ignored (no crouch).
      if (dt < 500 && dy < -25 && Math.abs(dy) > Math.abs(dx)) {
        jump();
      } else if (dt < 250 && Math.abs(dx) < 20 && Math.abs(dy) < 20) {
        jump();
      }
    };
    canvas.addEventListener("touchstart", onTouchStart, { passive: true });
    canvas.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchend", onTouchEnd);
      const s = stateRef.current;
      if (s?.rafId) cancelAnimationFrame(s.rafId);
    };
  }, [initState, jump]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="game-canvas"
      className="canvas-frame game-canvas-responsive"
      style={{ aspectRatio: `${LOGICAL_W}/${LOGICAL_H}` }}
    />
  );
});

export default GameCanvas;
